import { useSyncExternalStore } from 'react';
import { STANDARD_KATALOG } from './standard';
import type { AufgeloesterKatalog, KatalogHaeufigkeit, KatalogOverlay, KatalogSprache } from './typen';
import { katalogAufloesen, overlayNormalisieren } from './ueberlagerung';

/**
 * Laufzeit-Stand des Mängelkatalogs: aufgelöster Katalog (Standard +
 * Firmen-Overlay), das wirksame Overlay samt Herkunftsprofil und der
 * angemeldete Nutzer (für die Merkliste). Modul-Store mit
 * useSyncExternalStore; `katalogStand()` liefert dieselbe Referenz, solange
 * sich nichts ändert. Der Stand trägt den deutschen Katalog; den englischen
 * liefert `katalogFuer('en')` bzw. `useMangelKatalog('en')` (28.09.).
 */

export interface KatalogStand {
    katalog: AufgeloesterKatalog;
    overlay: KatalogOverlay | null;
    /** Profil, aus dem das wirksame Overlay stammt (Hinweis im Editor). */
    overlayProfil: { id: string; name: string } | null;
    nutzerId: string | null;
    /** Firmenweite Häufigkeit (Nr. 132) für die Reihenfolge der Auswahl; null = unbekannt. */
    haeufigkeit: KatalogHaeufigkeit | null;
}

const zuhoerer = new Set<() => void>();
let stand: KatalogStand | null = null;
/**
 * Zählt jede wirksame Overlay-Änderung. `ladeMangelKatalog` merkt sich den
 * Wert beim Start und übernimmt sein Ergebnis nur, wenn in der Zwischenzeit
 * niemand (z. B. der Admin-Editor) ein Overlay gesetzt hat (Codex-Review 27.09.).
 */
let overlayZaehler = 0;

/** Aktueller Stand des Overlay-Zählers (siehe oben). */
export function overlayVersion(): number {
    return overlayZaehler;
}

const grundstand = (): KatalogStand => ({
    katalog: katalogAufloesen(STANDARD_KATALOG, null),
    overlay: null,
    overlayProfil: null,
    nutzerId: null,
    haeufigkeit: null,
});

function melden(): void {
    for (const f of [...zuhoerer]) {
        try {
            f();
        } catch {
            // Ein Zuhörer darf die anderen nicht aufhalten.
        }
    }
}

/** Zuhörer für Änderungen am Stand (auch außerhalb von React); liefert die Abmeldung. */
export function katalogAbonnieren(f: () => void): () => void {
    zuhoerer.add(f);
    return () => {
        zuhoerer.delete(f);
    };
}

export function katalogStand(): KatalogStand {
    if (!stand) stand = grundstand();
    return stand;
}

export function useKatalogStand(): KatalogStand {
    return useSyncExternalStore(katalogAbonnieren, katalogStand, katalogStand);
}

/**
 * Englischer Katalog (28.09.), aufgelöst beim ersten Bedarf und gemerkt,
 * solange das Overlay dasselbe Objekt ist — so bleibt die Referenz stabil
 * (useSyncExternalStore, Such-Index je Katalog).
 */
let englischerKatalog: { overlay: KatalogOverlay | null; katalog: AufgeloesterKatalog } | null = null;

/** Aufgelöster Katalog in der Katalogsprache; 'de' ist `katalogStand().katalog`. */
export function katalogFuer(sprache: KatalogSprache = 'de'): AufgeloesterKatalog {
    const st = katalogStand();
    if (sprache !== 'en') return st.katalog;
    if (!englischerKatalog || englischerKatalog.overlay !== st.overlay) {
        englischerKatalog = { overlay: st.overlay, katalog: katalogAufloesen(STANDARD_KATALOG, st.overlay, 'en') };
    }
    return englischerKatalog.katalog;
}

export function useMangelKatalog(sprache: KatalogSprache = 'de'): AufgeloesterKatalog {
    const schnappschuss = () => katalogFuer(sprache);
    return useSyncExternalStore(katalogAbonnieren, schnappschuss, schnappschuss);
}

const gleichesProfil = (a: KatalogStand['overlayProfil'], b: KatalogStand['overlayProfil']): boolean =>
    a === b || (!!a && !!b && a.id === b.id && a.name === b.name);

const gleichesOverlay = (a: KatalogOverlay | null, b: KatalogOverlay | null): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
};

/** Setzt das Firmen-Overlay (wird normalisiert) und löst den Katalog neu auf. Gleiches Overlay → keine Meldung. */
export function setzeKatalogOverlay(overlay: KatalogOverlay | null, profil: { id: string; name: string } | null): void {
    const alt = katalogStand();
    const o = overlay ? overlayNormalisieren(overlay) : null;
    const p = profil && typeof profil.id === 'string' ? { id: profil.id, name: typeof profil.name === 'string' ? profil.name : '' } : null;
    const overlayGleich = gleichesOverlay(alt.overlay, o);
    if (overlayGleich && gleichesProfil(alt.overlayProfil, p)) return;
    overlayZaehler++;
    stand = {
        ...alt,
        katalog: overlayGleich ? alt.katalog : katalogAufloesen(STANDARD_KATALOG, o),
        overlay: overlayGleich ? alt.overlay : o,
        overlayProfil: p,
    };
    melden();
}

/** Neuer Nutzer → Häufigkeit leeren: sie gehört zur Firma des bisherigen (Codex-Review 28.09.). */
export function setzeKatalogNutzer(nutzerId: string | null): void {
    const alt = katalogStand();
    const id = typeof nutzerId === 'string' && nutzerId ? nutzerId : null;
    if (alt.nutzerId === id) return;
    stand = { ...alt, nutzerId: id, haeufigkeit: null };
    melden();
}

/** Häufigkeit der Firma (haeufigkeit.ts); dieselbe Referenz → keine Meldung. */
export function setzeKatalogHaeufigkeit(haeufigkeit: KatalogHaeufigkeit | null): void {
    const alt = katalogStand();
    if (alt.haeufigkeit === haeufigkeit) return;
    stand = { ...alt, haeufigkeit };
    melden();
}

/** Beim Abmelden: Standard ohne Overlay, kein Nutzer, keine Häufigkeit. */
export function katalogStandZuruecksetzen(): void {
    const alt = katalogStand();
    if (!alt.overlay && !alt.overlayProfil && alt.nutzerId === null && !alt.haeufigkeit) return;
    stand = grundstand();
    melden();
}
